import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import HeroParticles from './HeroParticles';
import '../styles/HeroSection.css';

const HeroSection = ({
  title,
  titleHighlight,
  subtitle,
  description,
  ctaText,
  ctaLink,
  variant = 'default'
}) => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' }
    }
  };

  return (
    <section className={`hero-section hero-section--${variant}`}>
      {/* Particles Background */}
      <HeroParticles />

      {/* E-shaped Letter Background */}
      <div className="hero-spiral">
        <svg
          viewBox="0 0 300 250"
          preserveAspectRatio="xMidYMid slice"
          className="spiral-svg"
        >
          {/* Gradient definition */}
          <defs>
            <linearGradient id="spiralGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#06b6d4" />
              <stop offset="50%" stopColor="#0ea5e9" />
              <stop offset="100%" stopColor="#3b82f6" />
            </linearGradient>
          </defs>

          {/* Letter E shape - Spiral style - Flipped to face content */}
          {/* Vertical bar with curve */}
          <path
            d="M 240 40 L 240 210 Q 220 220, 200 210 L 200 50 Q 220 40, 240 50 Z"
            fill="url(#spiralGradient)"
            opacity="0.95"
          />

          {/* Top horizontal bar - curved */}
          <path
            d="M 240 40 Q 220 30, 180 40 L 160 50 Q 200 45, 200 50 L 200 50 L 240 50 Z"
            fill="url(#spiralGradient)"
            opacity="0.9"
          />

          {/* Top right corner spiral */}
          <circle cx="120" cy="40" r="25" fill="#06b6d4" opacity="0.7" />
          <path
            d="M 160 50 Q 140 40, 120 40"
            stroke="#0ea5e9"
            strokeWidth="20"
            fill="none"
            opacity="0.6"
            strokeLinecap="round"
          />

          {/* Middle horizontal bar - curved */}
          <path
            d="M 240 120 Q 220 110, 170 120 L 160 130 Q 200 120, 200 125 L 200 125 L 240 125 Z"
            fill="url(#spiralGradient)"
            opacity="0.85"
          />

          {/* Middle right corner spiral */}
          <circle cx="140" cy="120" r="20" fill="#0ea5e9" opacity="0.6" />
          <path
            d="M 170 125 Q 150 115, 140 120"
            stroke="#06b6d4"
            strokeWidth="18"
            fill="none"
            opacity="0.5"
            strokeLinecap="round"
          />

          {/* Bottom horizontal bar - curved */}
          <path
            d="M 240 200 Q 220 210, 160 200 L 160 210 Q 200 215, 240 210 Z"
            fill="url(#spiralGradient)"
            opacity="0.9"
          />

          {/* Bottom right corner spiral */}
          <circle cx="120" cy="210" r="25" fill="#3b82f6" opacity="0.7" />
          <path
            d="M 160 205 Q 140 210, 120 210"
            stroke="#0ea5e9"
            strokeWidth="20"
            fill="none"
            opacity="0.6"
            strokeLinecap="round"
          />
        </svg>
      </div>

      {/* Content */}
      <div className="container">
        <motion.div
          className="hero-content"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Title with highlight */}
          <motion.h1 className="hero-title" variants={itemVariants}>
            {title}
            {titleHighlight && (
              <span className="hero-highlight">{titleHighlight}</span>
            )}
          </motion.h1>

          {/* Subtitle */}
          {subtitle && (
            <motion.p className="hero-subtitle" variants={itemVariants}>
              {subtitle}
            </motion.p>
          )}

          {/* Description */}
          {description && (
            <motion.p className="hero-description" variants={itemVariants}>
              {description}
            </motion.p>
          )}

          {/* CTA Button */}
          {ctaText && ctaLink && (
            <motion.div variants={itemVariants}>
              <a href={ctaLink} className="btn btn-primary hero-cta">
                {ctaText}
                <ArrowRight size={18} />
              </a>
            </motion.div>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;